import { omitBy } from "lodash";
import { isBlank } from "#/shared/lib/type-checks";

/**
 * Calls `append(name, value)` for each scalar under its Rails-bracketed name: `("f", { ids: [1, 2] })` → `f[ids][]=1`, `f[ids][]=2`.
 * Values accepted by `skip` are omitted with their children; `indexedObjects` keys object-array entries by index (`f[0][key]`).
 */
export function appendRailsParameter(append, name, value, { skip = () => false, indexedObjects = false } = {}) {
  if (skip(value)) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      const position = indexedObjects && item !== null && typeof item === "object" ? index : "";
      const key = name.endsWith("[]") ? name : `${name}[${position}]`;
      appendRailsParameter(append, key, item, { skip, indexedObjects });
    });
  } else if (value !== null && typeof value === "object") {
    Object.entries(value).forEach(([key, item]) =>
      appendRailsParameter(append, name ? `${name}[${key}]` : key, item, { skip, indexedObjects }),
    );
  } else {
    append(name, value);
  }
}

function appendParameter(searchParams, name, value) {
  appendRailsParameter(searchParams.append.bind(searchParams), name, value, { skip: isBlank });
}

/**
 * Returns the parameter name with one trailing `[]` removed.
 *
 * @finderRewrite
 */
export function finderParameterFamily(name) {
  return name.replace(/\[\]$/, "");
}

/**
 * Returns whether `name` matches `family` (ignoring its trailing `[]`) or one of its nested bracket keys.
 *
 * @finderRewrite
 */
export function ownsFinderParameter(family, name) {
  const baseName = finderParameterFamily(family);
  return name === baseName || name.startsWith(`${baseName}[`);
}

function removeParameterFamily(searchParams, name) {
  [...searchParams.keys()].forEach((key) => {
    if (ownsFinderParameter(name, key)) {
      searchParams.delete(key);
    }
  });
}

function appendFlatValue(query, name, value) {
  function append(key, item) {
    if (key.endsWith("[]")) {
      query[key] = [...(query[key] ?? []), String(item ?? "")];
    } else {
      query[key] = String(item ?? "");
    }
  }

  appendRailsParameter(append, name, value, { indexedObjects: true });
}

/**
 * Returns `params` as a router query: Rails-bracketed names holding strings, `name[]` holding arrays.
 * Top-level blank values are dropped: `{ zip: 92101, categories: ["7"], location: null }` →
 * `{ zip: "92101", "categories[]": ["7"] }`.
 *
 * @finderRewrite
 */
export function flattenFinderParams(params) {
  const query = {};
  Object.entries(omitBy(params, isBlank)).forEach(([name, value]) => appendFlatValue(query, name, value));
  return query;
}

const rawSearch = Symbol("rawSearch");

/**
 * Returns the router query for a search string, in Vue Router's query shape: a bare key is `null`, `key=` is
 * `""`, a repeated key or a `name[]` key holds an array. `"preview&zip=1&tag=a&tag=b"` →
 * `{ preview: null, zip: "1", tag: ["a", "b"] }`. The query remembers the string, so `stringifyFinderQuery`
 * writes an address the finder has not changed back as it was.
 *
 * @finderRewrite
 */
export function parseFinderQuery(search) {
  const raw = String(search).replace(/^\?/, "");
  const query = {};
  raw
    .split("&")
    .filter(Boolean)
    .forEach((pair) => {
      const [[name, value]] = new URLSearchParams(pair);
      const entry = pair.includes("=") ? value : null;

      if (Object.hasOwn(query, name)) {
        query[name] = [...[query[name]].flat(), entry];
      } else {
        query[name] = name.endsWith("[]") ? [entry] : entry;
      }
    });
  Object.defineProperty(query, rawSearch, { value: raw });
  return query;
}

function encodePair(name, value) {
  const pair = new URLSearchParams([[name, value ?? ""]]).toString();
  return value === null ? pair.slice(0, -1) : pair;
}

/**
 * Returns the search string, without `?`, for a router query in Vue Router's query shape. A query
 * `parseFinderQuery` returned is written as it was read. Otherwise names are sorted, `null` is written as a bare
 * key, `""` as `key=`, `undefined` is skipped, and each array value is written in order under the name as it
 * stands: `{ tag: ["b", "a"], preview: null, "ids[]": ["1"] }` → `"ids%5B%5D=1&preview&tag=b&tag=a"`.
 *
 * @finderRewrite
 */
export function stringifyFinderQuery(query) {
  if (Object.hasOwn(query, rawSearch)) {
    return query[rawSearch];
  }

  return Object.keys(query)
    .sort()
    .flatMap((name) =>
      [query[name]]
        .flat()
        .filter((value) => value !== undefined)
        .map((value) => encodePair(name, value)),
    )
    .join("&");
}

/**
 * Decodes Rails-style finder criteria while retaining repeated parameter names.
 *
 * @finderRewrite
 */
export function decodeFinderParams(search) {
  const query = new URLSearchParams(search);

  return Object.fromEntries(
    [...new Set(query.keys())].map((name) => {
      const values = query.getAll(name);
      return [name, name.endsWith("[]") || values.length > 1 ? values : values[0]];
    }),
  );
}

/**
 * Replaces finder-owned criteria without changing unrelated query parameters.
 *
 * @finderRewrite
 */
export function replaceFinderParams(searchParams, params) {
  Object.entries(params).forEach(([name, value]) => {
    removeParameterFamily(searchParams, name);
    appendParameter(searchParams, name, value);
  });
}
